import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, defaultLanguage, supportedLanguages, getTranslation } from './translations';

// Create Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get saved language from localStorage
    const saved = localStorage.getItem('wallet_language');
    if (saved && supportedLanguages.find(l => l.code === saved)) {
      return saved;
    }
    
    // Try to detect browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && supportedLanguages.find(l => l.code === browserLang)) {
      return browserLang;
    }
    
    return defaultLanguage;
  });

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('wallet_language', language);
  }, [language]);

  // Translation function
  const t = useCallback((key) => {
    return getTranslation(key, language);
  }, [language]);

  // Set language function
  const setLanguage = useCallback((lang) => {
    if (supportedLanguages.find(l => l.code === lang)) {
      setLanguageState(lang);
    }
  }, []);

  // Get current language info
  const currentLanguage = supportedLanguages.find(l => l.code === language) || supportedLanguages[0];

  const value = {
    language,
    setLanguage,
    t,
    currentLanguage,
    supportedLanguages,
    translations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Export for direct access
export { supportedLanguages, defaultLanguage };
